// client/src/components/pages/LexiStar/Reports/TrackSketch.tsx
//
// Planned-versus-actual map for paper (ST-00188 M4-R2; LS-09 §5.5, §6). The
// renderer prints on the internal network (no basemap tiles) and a WebGL
// canvas does not survive printing reliably, so the printed report draws the
// same layers as the on-screen map (Approvals/CorridorMap) as a plain SVG:
// planned route thin solid blue (a replaced plan dashed grey), actual track
// thick charcoal, deviations magenta — approved dashed, unapproved solid —
// stops as rings, source / destination labelled, a scale bar and north arrow.
// Equirectangular projection around the drawing's mid-latitude: at trip scale
// the distortion is far below the line widths.

import { useMemo } from 'react';

type Position = [number, number];

export interface SketchLine {
  geometry: unknown;
  replaced?: boolean;
}

export interface SketchDeviation {
  geometry: unknown;
  approved: boolean;
}

export interface SketchPoint {
  lat: number;
  lon: number;
  label?: string;
}

const W = 640;
const H = 380;
const PAD = 26;
const MAX_POINTS = 1500;
const KM_PER_DEG = 111.32;

/** Every line of a GeoJSON-ish value (Feature, FeatureCollection, (Multi)LineString, point list). */
export function linesOf(x: unknown): Position[][] {
  if (!x || typeof x !== 'object') return [];
  if (Array.isArray(x)) {
    const pts = (x as Array<Record<string, unknown>>)
      .map((p) => [Number(p.lon ?? p.lng), Number(p.lat)] as Position)
      .filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat));
    return pts.length ? [pts] : [];
  }
  const o = x as Record<string, unknown>;
  switch (o.type) {
    case 'Feature':
      return linesOf(o.geometry);
    case 'FeatureCollection':
      return (Array.isArray(o.features) ? o.features : []).flatMap((f) => linesOf(f));
    case 'LineString':
      return Array.isArray(o.coordinates) ? [o.coordinates as Position[]] : [];
    case 'MultiLineString':
      return Array.isArray(o.coordinates) ? (o.coordinates as Position[][]) : [];
    default:
      return o.geometry ? linesOf(o.geometry) : [];
  }
}

const thin = (pts: Position[]): Position[] => {
  if (pts.length <= MAX_POINTS) return pts;
  const step = Math.ceil(pts.length / MAX_POINTS);
  const out = pts.filter((_, i) => i % step === 0);
  const last = pts[pts.length - 1];
  if (last && out[out.length - 1] !== last) out.push(last);
  return out;
};

const NICE_KM = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500];

export default function TrackSketch({
  planned = [],
  track = null,
  deviations = [],
  stops = [],
  sites = [],
  label = 'Planned versus actual',
}: {
  planned?: SketchLine[];
  track?: unknown;
  deviations?: SketchDeviation[];
  stops?: SketchPoint[];
  sites?: SketchPoint[];
  label?: string;
}) {
  const drawing = useMemo(() => {
    const plannedLines = planned.flatMap((p) =>
      linesOf(p.geometry).map((l) => ({ pts: thin(l), replaced: !!p.replaced }))
    );
    const trackLines = linesOf(track).map(thin);
    const devLines = deviations.flatMap((d) =>
      linesOf(d.geometry).map((l) => ({ pts: thin(l), approved: d.approved }))
    );
    const all: Position[] = [
      ...plannedLines.flatMap((l) => l.pts),
      ...trackLines.flat(),
      ...devLines.flatMap((l) => l.pts),
      ...stops.map((s) => [s.lon, s.lat] as Position),
      ...sites.map((s) => [s.lon, s.lat] as Position),
    ].filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat));
    if (!all.length) return null;

    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    all.forEach(([lon, lat]) => {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
    const kx = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180);
    const spanX = Math.max((maxLon - minLon) * kx, 0.005);
    const spanY = Math.max(maxLat - minLat, 0.005);
    const scale = Math.min((W - 2 * PAD) / spanX, (H - 2 * PAD) / spanY);
    const offX = (W - spanX * scale) / 2;
    const offY = (H - spanY * scale) / 2;
    const px = ([lon, lat]: Position): [number, number] => [
      offX + (lon - minLon) * kx * scale,
      H - (offY + (lat - minLat) * scale),
    ];
    const path = (pts: Position[]) =>
      pts
        .map(px)
        .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
        .join(' ');

    // Scale bar: the nice length nearest a fifth of the width.
    const pxPerKm = scale / KM_PER_DEG;
    const targetKm = (W / 5) / pxPerKm;
    const km = NICE_KM.reduce((best, k) => (Math.abs(k - targetKm) < Math.abs(best - targetKm) ? k : best), NICE_KM[0]!);
    return {
      planned: plannedLines.map((l) => ({ d: path(l.pts), replaced: l.replaced })),
      track: trackLines.map(path),
      deviations: devLines.map((l) => ({ d: path(l.pts), approved: l.approved })),
      stops: stops.map((s) => px([s.lon, s.lat])),
      sites: sites.map((s) => ({ at: px([s.lon, s.lat]), label: s.label || '' })),
      bar: { km, px: km * pxPerKm },
    };
  }, [planned, track, deviations, stops, sites]);

  if (!drawing) {
    return <p className="lxr-empty">No planned route or track to draw yet.</p>;
  }

  return (
    <figure style={{ margin: 0 }}>
      <svg
        className="lxr-sketch"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        data-testid="track-sketch"
      >
        {drawing.planned
          .filter((l) => l.replaced)
          .map((l, i) => (
            <polyline
              key={`r${i}`}
              points={l.d}
              fill="none"
              stroke="var(--lxr-replaced, #94a3b8)"
              strokeWidth={2}
              strokeDasharray="6 4"
              strokeLinecap="round"
              data-layer="replaced"
              data-testid="layer-replaced"
            />
          ))}
        {drawing.planned
          .filter((l) => !l.replaced)
          .map((l, i) => (
            <polyline
              key={`p${i}`}
              points={l.d}
              fill="none"
              stroke="var(--lxr-planned, #2563eb)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              data-layer="planned"
              data-testid="layer-planned"
            />
          ))}
        {drawing.track.map((d, i) => (
          <polyline
            key={`t${i}`}
            points={d}
            fill="none"
            stroke="var(--lxr-actual, #334155)"
            strokeOpacity={0.85}
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
            data-layer="actual"
              data-testid="layer-actual"
          />
        ))}
        {drawing.deviations.map((l, i) => (
          <polyline
            key={`d${i}`}
            points={l.d}
            fill="none"
            stroke="var(--lxr-deviation, #c026d3)"
            strokeWidth={4.5}
            strokeDasharray={l.approved ? '7 4' : undefined}
            strokeLinecap="round"
            data-layer={l.approved ? 'deviation-approved' : 'deviation-unapproved'}
            data-testid={l.approved ? 'layer-deviation-approved' : 'layer-deviation-unapproved'}
          />
        ))}
        {drawing.stops.map(([x, y], i) => (
          <circle
            key={`s${i}`}
            cx={x}
            cy={y}
            r={4.5}
            fill="#ffffff"
            stroke="var(--lxr-deviation, #c026d3)"
            strokeWidth={2}
            data-layer="stop"
              data-testid="layer-stop"
          />
        ))}
        {drawing.sites.map((s, i) => (
          <g key={`site${i}`} data-layer="site"
              data-testid="layer-site">
            <rect x={s.at[0] - 6} y={s.at[1] - 6} width={12} height={12} fill="#1d2126" />
            <text x={s.at[0] + 10} y={s.at[1] + 4} fontSize={12} fontWeight={600} fill="#1d2126" stroke="#ffffff" strokeWidth={3} paintOrder="stroke">
              {s.label}
            </text>
          </g>
        ))}
        <g transform={`translate(${PAD}, ${H - 14})`} aria-hidden="true">
          <line x1={0} y1={0} x2={drawing.bar.px} y2={0} stroke="#1d2126" strokeWidth={2} />
          <line x1={0} y1={-4} x2={0} y2={4} stroke="#1d2126" strokeWidth={1.5} />
          <line x1={drawing.bar.px} y1={-4} x2={drawing.bar.px} y2={4} stroke="#1d2126" strokeWidth={1.5} />
          <text x={drawing.bar.px + 6} y={4} fontSize={11} fill="#4a5059">
            {drawing.bar.km} km
          </text>
        </g>
        <g transform={`translate(${W - 22}, 24)`} aria-hidden="true">
          <path d="M0 -12 L6 6 L0 2 L-6 6 Z" fill="#1d2126" />
          <text x={0} y={18} fontSize={10} textAnchor="middle" fill="#4a5059">
            N
          </text>
        </g>
      </svg>
      <ul className="lxr-legend" aria-label="Map legend">
        <li style={{ color: '#2563eb' }}>
          <i /> <span className="lxr-muted">Planned</span>
        </li>
        {drawing.planned.some((l) => l.replaced) && (
          <li style={{ color: '#94a3b8' }}>
            <i style={{ borderTopStyle: 'dashed' }} /> <span className="lxr-muted">Replaced plan</span>
          </li>
        )}
        <li style={{ color: '#334155' }}>
          <i style={{ borderTopWidth: 4 }} /> <span className="lxr-muted">Actual</span>
        </li>
        <li style={{ color: '#c026d3' }}>
          <i style={{ borderTopWidth: 4 }} /> <span className="lxr-muted">Unapproved deviation</span>
        </li>
        <li style={{ color: '#c026d3' }}>
          <i style={{ borderTopWidth: 3, borderTopStyle: 'dashed' }} /> <span className="lxr-muted">Approved detour</span>
        </li>
        {drawing.stops.length > 0 && (
          <li>
            <span aria-hidden="true" style={{ color: '#c026d3' }}>○</span> <span className="lxr-muted">Stop</span>
          </li>
        )}
      </ul>
    </figure>
  );
}
